import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "filterSize",
    "filterColour",
    "productGrid",
    "productCard",
    "noProductMessage",
    "productCount",
    "sortSelect",
    "selectedSizesContainer",
    "selectedColoursContainer",
    "selectedPriceContainer",
    "sizeCheckbox",
    "colourCheckbox",
    "colourDisabledOverlay",
    "loadMoreSpinner"
  ];

  filterSizes = [];
  filterColours = [];
  minPrice = null;
  maxPrice = null;
  sortOrder = "featured";
  numberOfProducts = 0;

  connect() {
    console.log("Product Filter Controller Connected");
    this.renderSelectedFilters();
    this.filterProducts();
  }

  filterBySize(e) {
    const checkbox = e.target;
    if (checkbox.checked) {
      this.filterSizes.push(checkbox.value);
    } else {
      this.filterSizes = this.filterSizes.filter(v => v !== checkbox.value);
    }

    this.renderSelectedFilters();
    this.updateAvailableOptions();
    this.filterProducts();
  }

  filterByColour(e) {
    const checkbox = e.target;
    if (checkbox.checked) {
      // Store both name and hex
      this.filterColours.push({
        name: checkbox.value,
        hex: checkbox.dataset.hex || "#000000"
      });
    } else {
      this.filterColours = this.filterColours.filter(c => c.name !== checkbox.value);
    }

    this.renderSelectedFilters();
    this.updateAvailableOptions();
    this.filterProducts();
  }

  filterByPriceRange(event) {
    const nextMin = Number(event.detail.min);
    const nextMax = Number(event.detail.max);
    const { absoluteMin, absoluteMax } = this.absolutePriceBounds();

    if (nextMin === absoluteMin && nextMax === absoluteMax) {
      this.minPrice = null;
      this.maxPrice = null;
    } else {
      this.minPrice = nextMin;
      this.maxPrice = nextMax;
    }

    this.renderSelectedFilters();
    this.filterProducts();
  }

  sortProducts(event) {
    this.sortOrder = event.target.value;
    this.applySort();
  }

  refreshAfterLoadMore(event) {
    if (event.target.getAttribute("target") !== "product-grid") return;

    requestAnimationFrame(() => {
      this.updateAvailableOptions();
      this.filterProducts();
    });
  }

  showLoadMoreSpinner(event) {
    if (!this.hasLoadMoreSpinnerTarget) return;

    this.loadMoreSpinnerTarget.classList.remove("hidden");
    event.currentTarget.setAttribute("aria-busy", "true");
    event.currentTarget.classList.add("pointer-events-none", "opacity-70");
  }

  clearAllFilters() {
    this.filterSizes = [];
    this.filterColours = [];
    this.minPrice = null;
    this.maxPrice = null;
    this.sortOrder = "featured";

    if (this.hasSizeCheckboxTarget) {
      this.sizeCheckboxTargets.forEach((checkbox) => {
        checkbox.checked = false;
        checkbox.disabled = false;
        const parentDiv = checkbox.closest(".relative");
        if (parentDiv) {
          parentDiv.style.opacity = "1";
          parentDiv.style.pointerEvents = "auto";
        }
      });
    }

    if (this.hasColourCheckboxTarget) {
      this.colourCheckboxTargets.forEach((checkbox, index) => {
        checkbox.checked = false;
        checkbox.disabled = false;
        const parentDiv = checkbox.closest(".relative");
        if (parentDiv) {
          parentDiv.style.opacity = "1";
          parentDiv.style.pointerEvents = "auto";
        }

        const overlay = this.hasColourDisabledOverlayTarget ? this.colourDisabledOverlayTargets[index] : null;
        if (overlay) {
          overlay.classList.add("hidden");
        }
      });
    }

    if (this.hasSortSelectTarget) {
      this.sortSelectTarget.value = "featured";
    }

    this.resetPriceControls();
    this.renderSelectedFilters();
    this.updateAvailableOptions();
    this.filterProducts();
  }

  // Render selected filters in the containers
  renderSelectedFilters() {
    this.renderPills(this.selectedSizesContainerTarget, this.filterSizes);

    this.renderColourPills();

    if (this.hasSelectedPriceContainerTarget) {
      const priceRange = this.currentPriceRangeLabel();
      this.renderPills(this.selectedPriceContainerTarget, priceRange ? [priceRange] : []);
    }
  }

  renderPills(container, values) {
    if (!container) return;

    container.innerHTML = "";
    values.forEach((value) => {
      const div = document.createElement("div");
      div.textContent = value;
      div.className = "filter-size-pill-styling";
      container.appendChild(div);
    });
  }

  renderColourPills() {
    if (!this.hasSelectedColoursContainerTarget) return;

    this.selectedColoursContainerTarget.innerHTML = "";
    this.filterColours.forEach((colour) => {
      const div = document.createElement("div");
      div.className = "size-4 rounded-full border border-cinder/20";
      div.style.backgroundColor = colour.hex;
      div.title = colour.name;
      this.selectedColoursContainerTarget.appendChild(div);
    });
  }

  currentPriceRangeLabel() {
    if (this.minPrice === null || this.maxPrice === null) return null;

    return `${this.formatPrice(this.minPrice)} - ${this.formatPrice(this.maxPrice)}`;
  }

  formatPrice(value) {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
      maximumFractionDigits: 0
    }).format(value);
  }

  resetPriceControls() {
    const rangeSliderElement = this.element.querySelector('[data-controller~="range-slider"]');
    if (!rangeSliderElement) return;

    const { absoluteMin, absoluteMax } = this.absolutePriceBounds();
    const rangeSliderController = this.application.getControllerForElementAndIdentifier(rangeSliderElement, "range-slider");

    this.minPrice = null;
    this.maxPrice = null;

    if (rangeSliderController) {
      rangeSliderController.currentMinValue = absoluteMin;
      rangeSliderController.currentMaxValue = absoluteMax;
      rangeSliderController.syncUI();
      rangeSliderController.emitChange();
      return;
    }

    const minInput = rangeSliderElement.querySelector('[data-range-slider-target="minInput"]');
    const maxInput = rangeSliderElement.querySelector('[data-range-slider-target="maxInput"]');
    const minRange = rangeSliderElement.querySelector('[data-range-slider-target="minRange"]');
    const maxRange = rangeSliderElement.querySelector('[data-range-slider-target="maxRange"]');
    const progress = rangeSliderElement.querySelector('[data-range-slider-target="progress"]');

    if (minInput) minInput.value = absoluteMin;
    if (maxInput) maxInput.value = absoluteMax;
    if (minRange) minRange.value = absoluteMin;
    if (maxRange) maxRange.value = absoluteMax;
    if (progress) {
      progress.style.left = "0%";
      progress.style.right = "0%";
    }
  }

  absolutePriceBounds() {
    const rangeSliderElement = this.element.querySelector('[data-controller~="range-slider"]');

    return {
      absoluteMin: Number(rangeSliderElement?.dataset.rangeSliderMinValue || 0),
      absoluteMax: Number(rangeSliderElement?.dataset.rangeSliderMaxValue || 0)
    };
  }

  updateAvailableOptions() {
    // Get available sizes and colors based on current filters
    const availableSizes = new Set();
    const availableColours = new Set();

    this.productCardTargets.forEach(card => {
      const cardSizes = card.dataset.availableSizes
        ? card.dataset.availableSizes.split(",").map(s => parseFloat(s.trim()))
        : [];
      const cardColour = card.dataset.availableColour || "DefaultColour";

      // If sizes are filtered, check which colors are available for those sizes
      if (this.filterSizes.length > 0) {
        const matchesSize = this.filterSizes.some(size =>
          cardSizes.includes(parseFloat(size))
        );
        if (matchesSize) {
          availableColours.add(cardColour);
        }
      }

      // If colors are filtered, check which sizes are available for those colors
      if (this.filterColours.length > 0) {
        const matchesColour = this.filterColours.some(c => c.name === cardColour);
        if (matchesColour) {
          cardSizes.forEach(size => availableSizes.add(size));
        }
      }

      // If no filters are active, all options are available
      if (this.filterSizes.length === 0 && this.filterColours.length === 0) {
        availableColours.add(cardColour);
        cardSizes.forEach(size => availableSizes.add(size));
      }
    });

    // Update size checkboxes
    if (this.hasSizeCheckboxTarget) {
      this.sizeCheckboxTargets.forEach(checkbox => {
        const isAvailable = this.filterColours.length === 0 ||
                           availableSizes.has(parseFloat(checkbox.value));
        const parentDiv = checkbox.closest('.relative');

        if (!checkbox.checked) {
          checkbox.disabled = !isAvailable;
          if (parentDiv) {
            parentDiv.style.opacity = isAvailable ? '1' : '0.3';
            parentDiv.style.pointerEvents = isAvailable ? 'auto' : 'none';
          }
        }
      });
    }

    // Update colour checkboxes
    if (this.hasColourCheckboxTarget) {
      this.colourCheckboxTargets.forEach((checkbox, index) => {
        const isAvailable = this.filterSizes.length === 0 ||
                           availableColours.has(checkbox.value);
        const parentDiv = checkbox.closest('.relative');
        const overlay = this.colourDisabledOverlayTargets[index];

        if (!checkbox.checked) {
          checkbox.disabled = !isAvailable;
          if (parentDiv) {
            parentDiv.style.opacity = isAvailable ? '1' : '0.5';
            parentDiv.style.pointerEvents = isAvailable ? 'auto' : 'none';
          }
          if (overlay) {
            overlay.classList.toggle('hidden', isAvailable);
          }
        }
      });
    }
  }

  filterProducts() {
    this.numberOfProducts = 0;

    this.productCardTargets.forEach(card => {
      const cardSizes = card.dataset.availableSizes
        ? card.dataset.availableSizes.split(",").map(s => parseFloat(s.trim()))
        : [];
      const cardColour = card.dataset.availableColour || "DefaultColour";
      const cardPrice = Number(card.dataset.price || 0);

      const matchesSize =
        this.filterSizes.length === 0 ||
        this.filterSizes.some(size => cardSizes.includes(parseFloat(size)));

      const matchesColour =
        this.filterColours.length === 0 ||
        this.filterColours.some(c => c.name === cardColour);

      const matchesPrice =
        (this.minPrice === null || cardPrice >= this.minPrice) &&
        (this.maxPrice === null || cardPrice <= this.maxPrice);

      const show = matchesSize && matchesColour && matchesPrice;

      if (show) this.numberOfProducts++;
      card.style.display = show ? "block" : "none";
    });

    if (this.hasProductCountTarget) {
      this.productCountTarget.textContent = `${this.numberOfProducts} Products`;
    }

    if (this.hasNoProductMessageTarget) {
      this.noProductMessageTarget.classList.toggle("hidden", this.numberOfProducts > 0);
    }

    this.applySort();
  }

  applySort() {
    const cards = [...this.productCardTargets];

    const sortedCards = cards.sort((leftCard, rightCard) => {
      switch (this.sortOrder) {
      case "price_asc":
        return Number(leftCard.dataset.price || 0) - Number(rightCard.dataset.price || 0);
      case "price_desc":
        return Number(rightCard.dataset.price || 0) - Number(leftCard.dataset.price || 0);
      case "alpha_asc":
        return (leftCard.dataset.name || "").localeCompare(rightCard.dataset.name || "", undefined, { sensitivity: "base" });
      default:
        return Number(leftCard.dataset.originalIndex || 0) - Number(rightCard.dataset.originalIndex || 0);
      }
    });

    sortedCards.forEach((card) => {
      if (this.hasNoProductMessageTarget) {
        this.productGridTarget.insertBefore(card, this.noProductMessageTarget);
      } else {
        this.productGridTarget.appendChild(card);
      }
    });
  }
}
